<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { LOCALE_NAMES, QURAN_LOCALES } from '@/utils/quran/locale';

/** The site's languages behind one small button; the page decides what choosing one does. */
defineProps({
  buttonClass: { type: String, default: '' },
});

const emit = defineEmits(['select']);

const { t, locale } = useI18n();
const open = ref(false);
const root = ref(null);

function choose(lang) {
  open.value = false;
  if (lang !== locale.value) emit('select', lang);
}

function onOutside(event) {
  if (open.value && root.value && !root.value.contains(event.target)) open.value = false;
}

function onKey(event) {
  if (event.key === 'Escape') open.value = false;
}

onMounted(() => {
  document.addEventListener('click', onOutside);
  document.addEventListener('keydown', onKey);
});
onBeforeUnmount(() => {
  document.removeEventListener('click', onOutside);
  document.removeEventListener('keydown', onKey);
});
</script>

<template>
  <div ref="root" class="relative">
    <button type="button" :class="buttonClass" :aria-label="t('quran.nav.switchLanguage')" :aria-expanded="open" aria-haspopup="menu" @click="open = !open">
      {{ locale }}
    </button>
    <ul v-if="open" role="menu" class="absolute right-0 top-full z-50 mt-1 min-w-36 rounded-2xl border border-black/5 bg-white p-1 shadow-lg">
      <li v-for="lang in QURAN_LOCALES" :key="lang" role="none">
        <button
          type="button"
          role="menuitem"
          :lang="lang"
          class="w-full rounded-xl px-3 py-2 text-left text-sm font-semibold text-[#1c4848] hover:bg-[#1c4848]/5"
          :class="{ 'bg-[#1c4848]/5': lang === locale }"
          @click="choose(lang)"
        >
          {{ LOCALE_NAMES[lang] }}
        </button>
      </li>
    </ul>
  </div>
</template>
